import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, Search, X } from 'lucide-react';
import { apiFetch } from '@/lib/api';

type EmployeeMatch = {
  employee_id: string;
  display_name: string;
  primary_node_label: string | null;
};

type SearchResponse = { matches: EmployeeMatch[]; truncated: boolean };

type Props = {
  selectedIds: Set<string>;
  onChange: (next: Set<string>) => void;
  /** v0.4 TD-22 と同型: 読み取り専用表示。トリガーを無効化してパネル自体を開けなくする。 */
  disabled?: boolean;
};

/** 条件エンジンの employee_identity 属性値選択UI (v0.5)。NodeMultiSelect と対になる命名だが、
 *  社員は数百〜数千人規模になりうるため全件ロード+クライアント側フィルタは使わず、
 *  `GET /employees/search` によるサーバー側検索に切り替える (設計: docs/design/
 *  v05-condition-employee-identity-attribute-detail.md §0-3/§2-4)。
 *  選択済みIDの表示名解決 (画面を再度開いた場合) は `GET /employees/search?ids=...` で個別に引く。 */
export function EmployeeMultiSelect({ selectedIds, onChange, disabled }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [panelPos, setPanelPos] = useState<{ left: number; maxHeight: number; top: number | null; bottom: number | null } | null>(null);

  // 選択済みIDの表示名解決 (NodeMultiSelect と異なり、選択候補を全件保持していないため個別に引く必要がある)
  const selectedIdsKey = useMemo(() => [...selectedIds].sort().join(','), [selectedIds]);
  const { data: resolvedSelected, isLoading: isResolvingSelected } = useQuery<SearchResponse>({
    queryKey: ['employee-search-resolve', selectedIdsKey],
    queryFn: () => apiFetch<SearchResponse>(`/employees/search?ids=${encodeURIComponent(selectedIdsKey)}`),
    enabled: selectedIdsKey.length > 0,
    staleTime: 5 * 60 * 1000,
  });
  const resolvedNameById = useMemo(() => {
    const map = new Map<string, EmployeeMatch>();
    for (const m of resolvedSelected?.matches ?? []) map.set(m.employee_id, m);
    return map;
  }, [resolvedSelected]);

  // 入力1文字ごとの再検索でリクエストが飛びすぎないよう、300msデバウンスする
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(handle);
  }, [query]);

  const { data: searchResult, isFetching } = useQuery<SearchResponse>({
    queryKey: ['employee-search', debouncedQuery],
    queryFn: () => apiFetch<SearchResponse>(`/employees/search?q=${encodeURIComponent(debouncedQuery)}`),
    enabled: open && debouncedQuery.length > 0,
    staleTime: 30 * 1000,
  });

  const recomputePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const rect = trigger.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const margin = 8;
    const desiredHeight = 360;
    const spaceBelow = viewportHeight - rect.bottom - margin;
    const spaceAbove = rect.top - margin;
    const openUpward = spaceBelow < 220 && spaceAbove > spaceBelow;
    const maxHeight = Math.max(180, Math.min(desiredHeight, openUpward ? spaceAbove : spaceBelow));
    setPanelPos({
      left: Math.min(rect.left, window.innerWidth - 320 - margin),
      maxHeight,
      top: openUpward ? null : rect.bottom + margin,
      bottom: openUpward ? viewportHeight - rect.top + margin : null,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) { setPanelPos(null); return; }
    recomputePosition();
    const onReposition = () => recomputePosition();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
    };
  }, [open, recomputePosition]);

  useEffect(() => {
    if (!open) { setQuery(''); setDebouncedQuery(''); return; }
    const handle = setTimeout(() => searchInputRef.current?.focus(), 0);
    return () => clearTimeout(handle);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (panelRef.current?.contains(e.target as Node) || triggerRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setOpen(false); return; }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const focusable = Array.from(
        panelRef.current.querySelectorAll<HTMLElement>('button:not(:disabled), input, [tabindex]:not([tabindex="-1"])'),
      );
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
      triggerRef.current?.focus();
    };
  }, [open]);

  const toggleEmployee = useCallback((employeeId: string) => {
    const next = new Set(selectedIds);
    if (next.has(employeeId)) next.delete(employeeId);
    else next.add(employeeId);
    onChange(next);
  }, [selectedIds, onChange]);

  const matches = searchResult?.matches ?? [];

  return (
    <div className="relative inline-block w-full">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        aria-haspopup="true"
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 px-2 py-1 rounded border border-neutral-line bg-white text-xs hover:bg-neutral-panel focus-ring disabled:opacity-45 disabled:cursor-not-allowed disabled:hover:bg-white"
      >
        社員を検索して選択 <span className="tabular-nums text-brand-red font-semibold">({selectedIds.size})</span>
        <ChevronDown size={12} className="text-neutral-sub" />
      </button>

      {selectedIds.size > 0 && (
        // ⚠️ design-expertレビューMEDIUM反映: 大量選択時に縦へ際限なく伸びないよう高さを抑え、
        // 内部スクロールにする (NodeMultiSelect は選択チップをこの形で表示しないため新規に必要)。
        <div className="flex flex-wrap gap-1.5 mt-1.5 max-h-[120px] overflow-y-auto" role="group" aria-label="選択済みの社員">
          {[...selectedIds].map((employeeId) => {
            const resolved = resolvedNameById.get(employeeId);
            return (
              <span
                key={employeeId}
                className="inline-flex items-center gap-1 pl-2.5 pr-1 min-h-[32px] rounded-full text-xs bg-misespark-navy text-white"
              >
                {resolved
                  ? (
                    <span>
                      {resolved.display_name}
                      {resolved.primary_node_label && <span className="opacity-75"> ({resolved.primary_node_label})</span>}
                    </span>
                  )
                  // ⚠️ design-expertレビューHIGH反映: 表示名解決前に生の employeeId を見せない
                  // (社員番号ではなくUUID風のIDのため業務ユーザーには意味不明な文字列になる)
                  : <span className="opacity-75">{isResolvingSelected ? '読み込み中…' : employeeId}</span>}
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => toggleEmployee(employeeId)}
                    className="p-1 rounded-full hover:bg-white/20"
                    aria-label={`${resolved?.display_name ?? employeeId} を選択解除`}
                  >
                    <X size={12} />
                  </button>
                )}
              </span>
            );
          })}
        </div>
      )}

      {open && panelPos && createPortal(
        <div
          ref={panelRef}
          role="dialog"
          aria-label="社員検索パネル"
          style={{
            position: 'fixed', left: panelPos.left,
            top: panelPos.top ?? undefined, bottom: panelPos.bottom ?? undefined,
            maxHeight: panelPos.maxHeight,
          }}
          className="z-[60] w-[320px] max-w-[calc(100vw-2rem)] flex flex-col rounded-lg border border-neutral-line bg-white shadow-lg overflow-hidden"
        >
          <div className="border-b border-neutral-line px-3 py-2 flex items-center gap-2">
            <Search size={14} className="text-neutral-sub flex-shrink-0" />
            <input
              ref={searchInputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); setOpen(false); } }}
              placeholder="氏名・社員番号で検索"
              className="flex-1 text-sm bg-transparent focus:outline-none placeholder:text-neutral-sub"
              aria-label="社員検索"
            />
            {query && (
              <button type="button" onClick={() => setQuery('')} className="text-neutral-sub hover:text-brand-red" aria-label="検索クエリをクリア">
                <X size={14} />
              </button>
            )}
          </div>
          <div className="px-1 py-1 overflow-y-auto">
            {debouncedQuery.length === 0 ? (
              <div className="px-3 py-6 text-center text-xs text-neutral-sub">氏名または社員番号を入力してください</div>
            ) : isFetching ? (
              <div className="px-3 py-6 text-center text-xs text-neutral-sub">検索中…</div>
            ) : matches.length === 0 ? (
              <div className="px-3 py-6 text-center text-xs text-neutral-sub">該当する社員が見つかりません</div>
            ) : (
              <ul>
                {matches.map((m) => {
                  const isSelected = selectedIds.has(m.employee_id);
                  return (
                    <li key={m.employee_id}>
                      <button
                        type="button"
                        onClick={() => toggleEmployee(m.employee_id)}
                        className={`w-full flex items-center gap-2 px-2 py-1.5 min-h-[36px] rounded text-left text-sm hover:bg-neutral-panel ${isSelected ? 'bg-neutral-panel' : ''}`}
                      >
                        <input type="checkbox" tabIndex={-1} checked={isSelected} onChange={() => toggleEmployee(m.employee_id)} className="w-4 h-4 accent-brand-red pointer-events-none" />
                        <span className="flex-1 truncate">{m.display_name}</span>
                        {m.primary_node_label && <span className="text-[10px] text-neutral-sub shrink-0">{m.primary_node_label}</span>}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
            {searchResult?.truncated && (
              <p className="px-3 py-1.5 text-[10px] text-neutral-sub">
                候補が多すぎるため一部のみ表示しています。検索語を絞り込んでください。
              </p>
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
